import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Filters,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { Ambito } from '../ambiti/ambiti.service';
import { AziendaService } from '../azienda/azienda.service';
import { ESRS } from '../ESRS/esrs.service';
import { Settore } from '../settori/settori.service';
import { CategoriaStakeholders } from '../stakeholder/stakeholder.service';
import { WebService } from '../web/web.service';
import {
  DatiConsolidati,
  DoppiaSurvey,
  ElementiCheckati,
  ImpattoIro,
  Opportunita,
  Rischio,
} from './doppia-materialita.service';

@Injectable({
  providedIn: 'root',
})
export class DoppiaMaterialitaWebService {
  private readonly lang = '/it-IT';
  private _idAzienda: string | undefined = undefined;

  private readonly nomeWs = 'doppia-materialita';

  constructor(
    private readonly aziendaService: AziendaService,
    private readonly webService: WebService,
  ) {
    this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      this._idAzienda = azienda?.id;
    });
  }

  public get(
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/list',
      { page, pageSize, ricerca, filters, sortBy },
    );
  }

  public post(data: any): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/lang' + this.lang + '/crea',
      data,
    );
  }

  public delete(id: any): Observable<any> {
    return this.webService.doDelete(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/' + id + '/delete',
      {},
    );
  }

  public archivia(idModulo: any): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/' + idModulo + '/archivia',
      {},
    );
  }

  public disarchivia(idModulo: any, idSettore: string): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idModulo +
        '/settore/' +
        idSettore +
        '/ripristina',
      {},
    );
  }

  public put(id: string, materialita: any): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        id +
        '/lang' +
        this.lang +
        '/update',
      materialita,
    );
  }

  /**
   * Ritorna materialità corrispondete all'id
   * @param idDoppiaMaterialita
   * @returns
   */
  public getDoppiaMaterialitaById(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/' + idDoppiaMaterialita,
      {},
    );
  }

  /* ------------------------- T E M A T I C H E -------------------------------- */

  public getQuestionarioDoppiaMaterialita(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/survey',
      {},
    );
  }

  public putQuestionarioDoppiaMaterialita(
    idDoppiaMaterialita: string,
    idSurvey: string,
    ambiti: Ambito[],
    settori: Settore[],
    titolo: string,
    stato: string,
    tipo: string,
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/survey',
      {
        id: idSurvey,
        ambiti,
        settori,
        doppiaMaterialita: [idDoppiaMaterialita],
        aziende: [this._idAzienda],
        titolo,
        stato,
        tipo,
      },
    );
  }

  /**
   * Recuepra esrs per id tematica
   * @param idTematica
   * @returns
   */
  public getEsrsByIdDP(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
  ): Observable<any> {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/ambito/' +
        idAmbito +
        '/tematica/' +
        idTematica +
        '/selection-for-tematica',
      {},
    );
  }

  public putEsrsTematicaDP(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    esrs: ESRS[],
  ): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/ambito/' +
        idAmbito +
        '/tematica/' +
        idTematica +
        '/selection-for-tematica',
      esrs,
    );
  }

  /* ------------------------- I R O -------------------------------- */

  public getImpatti(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this._getIro('impatti', idDoppiaMaterialita, idAmbito, idTematica, idTema);
  }

  public getRischi(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this._getIro('rischi', idDoppiaMaterialita, idAmbito, idTematica, idTema);
  }

  public getOpportunita(
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    return this._getIro('opportunita', idDoppiaMaterialita, idAmbito, idTematica, idTema);
  }

  public putImpattiEffimeri(idDoppiaMaterialita: string, impatto: ImpattoIro) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/impatti-iro-effimeri',
      impatto,
    );
  }

  public putRischiEffimeri(idDoppiaMaterialita: string, rischio: Rischio) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/iro/rischi/ro-effimeri',
      rischio,
    );
  }

  public putOpportunitaEffimeri(idDoppiaMaterialita: string, opportunita: Opportunita) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/iro/opportunita/ro-effimeri',
      opportunita,
    );
  }

  public putCheckImpattiEffimeri(idDoppiaMaterialita: string, impattiCheckati: ElementiCheckati[]) {
    return this._putCheckIro('impatti', idDoppiaMaterialita, impattiCheckati);
  }

  public putCheckRischiEffimeri(idDoppiaMaterialita: string, rischiCheckati: ElementiCheckati[]) {
    return this._putCheckIro('rischi', idDoppiaMaterialita, rischiCheckati);
  }

  public putCheckOpportunitaEffimeri(
    idDoppiaMaterialita: string,
    opportunitaCheckati: ElementiCheckati[],
  ) {
    return this._putCheckIro('opportunita', idDoppiaMaterialita, opportunitaCheckati);
  }

  public getValidatoriIro(idDoppiaMaterialita: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/get-validatori-iro',
      {},
    );
  }

  public checkValidatoriIro(
    idDoppiaMaterialita: string,
    categorieStakeholders: CategoriaStakeholders[],
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/check-validatori-iro',
      categorieStakeholders,
    );
  }

  public validaIroSelezionati(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/valida-iro-selezionati',
      {},
    );
  }

  public listCruscottoValidatori(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/list',
      { page, pageSize, ricerca, filters, sortBy },
    );
  }

  public getTemplateMailValidazioni(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/get-template',
      {},
    );
  }

  public updateTemplateMailValidazioni(idDoppiaMaterialita: string, templateMail: any) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/aggiorna-template',
      templateMail,
    );
  }

  public testInvioMailValidazioni(messaggio: string, email: string, idDoppiaMaterialita: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/test-invio-mail',
      {
        messaggio,
        email,
      },
    );
  }

  public getTemplateMail(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/cruscotto/get-template',
      {},
    );
  }

  public updateTemplateMail(idDoppiaMaterialita: string, templateMail: any) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/cruscotto/aggiorna-template',
      templateMail,
    );
  }

  public testInvioMail(messaggio: string, email: string, idDoppiaMaterialita: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/cruscotto/test-invio-mail',
      {
        messaggio,
        email,
      },
    );
  }

  public getTotaliValidazioni(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/totali',
      {},
    );
  }

  public getVersioniQuestionarioValidazione(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/questionario-validazione/versioni-list',
      {},
    );
  }

  public inviaMailValidatori(idDoppiaMaterialita: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/invia-mail',
      {},
    );
  }

  public inviaMailValidatoreSingolo(idDoppiaMaterialita: string, idValidatore: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/validatore/' +
        idValidatore +
        '/invia-mail',
      {},
    );
  }

  public inviaNotificheValidatori(idDoppiaMaterialita: string) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/invia-notifica',
      {},
    );
  }

  public inviaNotificaValidatoreSingolo(idDoppiaMaterialita: string, idValidatore: string) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/validatore/' +
        idValidatore +
        '/invia-notifica',
      {},
    );
  }

  public pubblicaQuestionarioValidazione(idDoppiaMaterialita: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/pubblica',
      {},
    );
  }

  public getQuestionarioValidazioneDaCompilare(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/questionario-validazione/get',
      {},
    );
  }

  public getAnteprimaQuestionarioValidazioneDaCompilare(
    idAzienda: string,
    idDoppiaMaterialita: string,
  ) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/questionario-validazione/anteprima',
      {},
    );
  }

  public salvaQuestionarioValidazione(
    idDoppiaMaterialita: string,
    idValidatore: string,
    questionario: any,
  ) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/validatore/' +
        idValidatore +
        '/questionario-validazione/compila',
      questionario,
    );
  }

  public getQuestionarioValidazioneCompilato(idDoppiaMaterialita: string, idValidatore: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/validatore/' +
        idValidatore +
        '/questionario-validazione/get',
      {},
    );
  }

  public exportVersioneValidazione(idDoppiaMaterialita: string, versione: number) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/export/versione/' +
        versione,
      {},
    );
  }

  public getQuestionarioValidazioneDaEsterno(temporaryToken: string) {
    return this.webService.doGet('/api/v1/compila-questionario-di-validazione/get', {
      temporaryToken,
    });
  }

  public salvaQuestionarioValidazioneDaEsterno(temporaryToken: string, objEsterna: any) {
    return this.webService.doPut(
      '/api/v1/compila-questionario-di-validazione/compila',
      objEsterna,
      {
        temporaryToken,
      },
    );
  }

  public getStakeholders(idDoppiaMaterialita: string, tipo: string) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/stakeholders/list/doppia-materialita/' +
        idDoppiaMaterialita +
        '/tipologia/' +
        tipo,
      {},
    );
  }

  public putStakeholders(idDoppiaMaterialita: string, tipo: string, stakeholders: any) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/aggiorna-stakeholders/tipologia/' +
        tipo,
      stakeholders,
    );
  }

  public salvaPesiStakeholders(
    idDoppiaMaterialita: string,
    pesiStakeholders: { pesoShInterni: number; pesoShEsterni: number },
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/aggiorna-pesi-soglie',
      pesiStakeholders,
    );
  }

  public salvaSceltaCampione(
    idDoppiaMaterialita: string,
    tipologia: string,
    idCategoria: string,
    sceltaCampione: string,
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/aggiorna-stakeholders/tipologia/' +
        tipologia +
        '/categoria/' +
        idCategoria +
        '/campione',
      { body: sceltaCampione },
    );
  }

  public getEsitiCompilazioniUltimaVersione(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/esiti-compilazione',
      {},
    );
  }

  public getEsitiCompilazioniPerVersione(idDoppiaMaterialita: string, versione: number) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/validatori-iro/esiti-compilazione/versione/' +
        versione,
      {},
    );
  }

  private _getIro(
    iroType: string,
    idDoppiaMaterialita: string,
    idAmbito: string,
    idTematica: string,
    idTema?: string,
  ) {
    const pathTema = idTema ? '/tema/' + idTema : '';
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/ambito/' +
        idAmbito +
        '/tematica/' +
        idTematica +
        pathTema +
        '/iro/' +
        iroType +
        '/get-nested-iro',
      {},
    );
  }

  private _putCheckIro(
    iroType: string,
    idDoppiaMaterialita: string,
    iroCheckati: ElementiCheckati[],
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/iro/' +
        iroType +
        '/check-iro',
      iroCheckati,
    );
  }

  /**
   * Ritorna la survey della doppia materialità
   * @param idDoppiaMaterialita
   * @returns
   */
  public getSurveyDP(id: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/' + id + '/doppia-survey',
      {},
    );
  }

  public getCategorieSHperIRO(idDoppiaMaterialita: string, idIro: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/get-categoria-assegnata-iro/' +
        idIro,
      {},
    );
  }

  public putCategorieSHperIRO(objSalvataggio: any) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        objSalvataggio.idDoppiaMaterialita +
        '/assegna-iro/type/' +
        objSalvataggio.iroType,
      {
        idAmbito: objSalvataggio.idAmbito,
        idTematica: objSalvataggio.idTematica,
        idTema: objSalvataggio.idTema,
        idIro: objSalvataggio.idIro,
        idCategorie: objSalvataggio.idCategorieSelezionate,
      },
    );
  }
  public validaCategorieSHperIRO(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/valida-assegnazioni-categoria',
      {},
    );
  }
  public getCriteriPerAssegnazione(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/get-criteri-di-assegnazione',
      {},
    );
  }

  public putCriteriAssegnazioniCategoria(
    idDoppiaMaterialita: string,
    idCategoria: string,
    tipologia: string,
    testoCriterio: string,
  ) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/aggiorna-criteri-di-assegnazione/categoria/' +
        idCategoria +
        '/tipologia/' +
        tipologia,
      { body: testoCriterio },
    );
  }

  public getCruscottoSurvey(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/list',
      { page, pageSize, ricerca, filters, sortBy },
    );
  }

  /**
   * Recupera il riepilogo paginato degli invii mail del cruscotto survey
   */
  public getRiepilogoInvioMail(
    idDoppiaMaterialita: string,
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/riepilogo-invio-mail',
      { page, pageSize, ricerca, filters, sortBy },
    );
  }

  /**
   * Invia la mail ad un singolo stakeholder del cruscotto survey
   */
  public postInvioMailSingoloSurvey(
    idDoppiaMaterialita: string,
    id: string,
  ): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/' +
        id +
        '/invio-singolo',
      { idDoppiaMaterialita, id },
    );
  }

  public putScadenzaSurvey(idDoppiaMaterialita: string, scadenzaSurvey: Date) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/scadenza-survey',
      { value: scadenzaSurvey },
    );
  }

  public getTotaliCruscottoSurvey(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/totali',
      {},
    );
  }

  public putTemplateMailSurvey(idDoppiaMaterialita: string, templateMail: any): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/cruscotto/aggiorna-template',
      templateMail,
      { idDoppiaMaterialita },
    );
  }

  public getTemplateMailSurvey(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/get-template',
      {},
    );
  }

  public getAnteprimaMaterialitaCompilataSurvey(
    idDoppiaMaterialita: string,
    idAzienda: string,
    idSurvey: string,
  ): Observable<any> {
    return this.webService.doGet(
      '/api/v1/azienda/' + idAzienda + '/' + this.nomeWs + '/' + idDoppiaMaterialita + '/anteprima',
      {},
    );
  }

  /**
   * Metodo che invia mail a tutti gli stakeholder che la devono ricevere (pre prendere il token e fare la compilazione esterna della survey)
   * @param idDoppiaMaterialita
   */
  public postInvioMailMultiploSurvey(idDoppiaMaterialita: string): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/invia-mail',
      {},
    );
  }

  /**
   * Metodo che invia una notifica singolarmente per stakeholder
   * @param idDoppiaMaterialita
   */
  public putInviaNotificaMultiploSurvey(idDoppiaMaterialita: string) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/invia-notifica',
    );
  }

  /* ------------- DOPPIA SURVEY ------------------ */
  public getDatiSurvey(
    idDoppiaMaterialita: string,
    idCategoriaStakeholder: string,
  ): Observable<any> {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/cruscotto/compilazione-interna/categoria/' +
        idCategoriaStakeholder,
      {
        idAzienda: this._idAzienda,
        idMaterialita: idDoppiaMaterialita,
        idCategoria: idCategoriaStakeholder,
      },
    );
  }

  public getDoppiaSurveyCompilata(
    idDoppiaMaterialita: string,
    idSurveyCompilato: string,
  ): Observable<any> {
    return this.webService.doGet(
      '/api/v1/lang' +
        this.lang +
        '/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/doppia-survey-compilate/survey-compilata/' +
        idSurveyCompilato +
        '/get',
      {},
    );
  }

  public postDatiDoppiaSurvey(
    idDoppiaMaterialita: string,
    idStakeholder: string,
    idSurvey: string,
    surveyCompilata: DoppiaSurvey,
  ): Observable<any> {
    return this.webService.doPost(
      '/api/v1/lang' +
        this.lang +
        '/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/doppia-survey-compilate/stakeholder/' +
        idStakeholder +
        '/survey/' +
        idSurvey +
        '/compila',
      surveyCompilata,
    );
  }

  public getRisultati(idDoppiaMaterialita: string): Observable<any> {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/analisi-risultati/risultati-doppia-survey',
      {},
    );
  }

  public consolidaRisultati(
    idDoppiaMaterialita: string,
    datiConsolidati: DatiConsolidati,
  ): Observable<any> {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/analisi-risultati/consolida-risultato',
      datiConsolidati,
    );
  }

  /**
   * Ritorna la Survey della Materialità associata per la quale è stata richiesta la compilazione
   * @param token informazioni sono reperite all'interno del temporary token ( dura 24h )
   * @returns
   */

  public getDoppiaSurveyEsterna(temporaryToken: string) {
    return this.webService.doGet('/api/v1/compila-doppia-survey/get', {
      temporaryToken: temporaryToken,
    });
  }

  /**
   * Manda la Survey della Materialità compilata per la quale è stata richiesta la compilazione
   * @param token informazioni sono reperite all'interno del temporary token ( dura 24h )
   * @param survey la survey che è  stata compilata
   * @returns
   */
  public postDoppiaSurveyEsterna(temporaryToken: any, survey: any) {
    return this.webService.doPut('/api/v1/compila-doppia-survey/compila', survey, {
      temporaryToken: temporaryToken,
    });
  }

  public pubblicaDoppiaSurvey(idDoppiaMaterialita: string) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/pubblica',
      {},
    );
  }

  public getValidazioneStepCruscottoSurvey(idDoppiaMaterialita: string): Observable<any> {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/analisi-risultati/valida-doppia-survey',
      {},
    );
  }

  public stampa(idDoppiaMaterialita: string) {
    return this.webService.doPut(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/' +
        this.nomeWs +
        '/' +
        idDoppiaMaterialita +
        '/stampa',
    );
  }

  /**
   * ritorna materialità pubblicate
   * @returns
   */
  public getDoppiaMaterialitaPubblicate(
    idSettore: string,
    idSottoSettori: string[],
  ): Observable<any> {
    return this.webService.doPost(
      '/api/v1/azienda/' + this._idAzienda + '/' + this.nomeWs + '/settore/' + idSettore + '/list',
      {
        idSottoSettori,
      },
    );
  }

  public exportAnalisiRisultati(idDoppiaMaterialita: string) {
    return this.webService.doGet(
      '/api/v1/azienda/' +
        this._idAzienda +
        '/doppia-materialita/' +
        idDoppiaMaterialita +
        '/analisi-risultati/export-excel',
      {},
    );
  }
}
